<template>
    <widget-card>
        <template #header>
            <Wrench class="widget-icon" />
            <span class="widget-card-header__title">
                {{ trans('dashboard.v2.preventative_maintenance.title') }}
            </span>
        </template>

        <!--
            Sticky band: subtitle + legend + TimelineHeader pin directly under
            the page-level filter bar. The bar is `sticky top-15` (60px) in
            DetailsPage but its height changes when the filter inputs wrap
            onto a second line (narrow viewports, sidebar expanded, more
            tabs), so we can't hard-code its bottom. `stickyBandTop` is
            measured from the live filter bar element via a ResizeObserver
            and kept in sync — `pt-6` below provides the breathing strip;
            anchoring flush eliminates the slit through which scrolling
            icons could peek. The inner element is the actual card surface
            (rounded + border + shadow), matching the maintainer-group
            cards below.
        -->
        <div class="sticky z-10 bg-white pt-6 pb-2" :style="{ top: `${stickyBandTop}px` }">
            <div ref="timelineRef" class="mx-2 rounded-card border border-gray-200 bg-white shadow-sm">
                <div class="px-4 pt-4 pb-3">
                    <p class="m-0 text-sm text-muted">
                        {{ trans('dashboard.v2.preventative_maintenance.subtitle') }}
                    </p>
                </div>
                <div class="flex items-center gap-4 px-4 pb-3 text-xs text-muted">
                    <span class="flex items-center gap-1.5">
                        <span
                            class="inline-flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-300 bg-compliance-unscheduled"
                        ></span>
                        {{ trans('dashboard.v2.preventative_maintenance.status.planned') }}
                    </span>
                    <span class="flex items-center gap-1.5">
                        <span class="inline-flex h-4 w-4 items-center justify-center rounded-full bg-pm-scheduled text-white">
                            <Hourglass class="h-2.5 w-2.5" />
                        </span>
                        {{ trans('dashboard.v2.preventative_maintenance.status.scheduled') }}
                    </span>
                    <span class="flex items-center gap-1.5">
                        <span class="inline-flex h-4 w-4 items-center justify-center rounded-full bg-compliance-approved text-white">
                            <Check class="h-2.5 w-2.5" />
                        </span>
                        {{ trans('dashboard.v2.preventative_maintenance.status.performed') }}
                    </span>
                    <span class="flex items-center gap-1.5">
                        <span class="inline-flex h-4 w-4 items-center justify-center rounded-full bg-compliance-inoperable text-white">
                            <Cross class="h-2 w-2" />
                        </span>
                        {{ trans('dashboard.v2.preventative_maintenance.status.not_performed') }}
                    </span>
                </div>
                <div v-if="showSkeleton" class="grid items-end" :style="skeletonGridStyle" aria-hidden="true">
                    <div class="px-4 pt-8 pb-3">
                        <SkeletonLoader class="m-0" />
                    </div>
                    <div class="flex items-end justify-center pt-8 pb-3">
                        <SkeletonLoader class="m-0" />
                    </div>
                    <div
                        v-for="(i, index) in visibleYears"
                        :key="i"
                        class="flex flex-col items-center justify-end pt-8 pb-3"
                        :class="index > 0 ? 'border-l border-gray-200' : ''"
                    >
                        <SkeletonLoader class="m-0 w-1/2" />
                    </div>
                    <div class="flex items-end justify-center pt-8 pb-3">
                        <SkeletonLoader class="m-0" />
                    </div>
                </div>
                <TimelineHeader
                    v-else-if="groups.length"
                    :years="years"
                    :current-year="currentYear"
                    :can-step-left="canStepLeft"
                    :can-step-right="canStepRight"
                    @step-left="shiftYears(-1)"
                    @step-right="shiftYears(1)"
                    @jump-to-today="anchorAround(currentYear)"
                />
            </div>
        </div>

        <div class="px-2 pt-4 pb-2">
            <div v-if="showSkeleton" class="space-y-4">
                <section v-for="card in 2" :key="card" class="overflow-hidden rounded-card border border-gray-200 bg-white" aria-hidden="true">
                    <header class="flex flex-col gap-1 px-6 py-4">
                        <SkeletonLoader class="m-0 w-40" />
                        <SkeletonLoader class="m-0 w-56" />
                    </header>
                    <div v-for="row in 2" :key="row" class="border-t border-gray-200">
                        <div class="grid items-center" :style="skeletonRowStyle">
                            <div class="flex flex-col gap-1 px-4 py-3">
                                <SkeletonLoader class="m-0 w-32" />
                                <SkeletonLoader class="m-0 w-40" />
                            </div>
                            <div
                                v-for="(_, yearIndex) in visibleYears"
                                :key="yearIndex"
                                class="flex items-center justify-around gap-1 px-2 py-2"
                                :class="yearIndex > 0 ? 'border-l border-gray-200' : ''"
                            >
                                <SkeletonLoader v-for="dot in 4" :key="dot" class="m-0 max-w-6" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <empty-widget-state v-else-if="!groups.length" :text="trans('dashboard.v2.preventative_maintenance.empty_state')" />
            <div v-else class="space-y-4">
                <MaintainerGroup v-for="group in groups" :key="group.maintainerId" :group="group" :years="years" />
            </div>
        </div>
    </widget-card>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { $http } from '@/bootstrap';
import { buildUrlWithQuery } from '@/Utils/buildUrlWithQuery';
import { eventBus, events } from '@/eventBus';
import { trans } from '@/mixins/i18n';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import Check from '@/../icons/Check.vue';
import Cross from '@/../icons/Cross.vue';
import Hourglass from '@/../icons/Hourglass.vue';
import Wrench from '@/../icons/Wrench.vue';
import TimelineHeader from '@/Pages/DashboardV2/Components/PreventativeMaintenance/TimelineHeader.vue';
import MaintainerGroup from '@/Pages/DashboardV2/Components/PreventativeMaintenance/MaintainerGroup.vue';
import {
    DEFAULT_VISIBLE_YEARS,
    ROW_HORIZONTAL_MARGIN_PX,
    computeVisibleYears,
    headerGridTemplate,
    rowGridTemplate,
    yearColumnMinForDensity,
} from '@/Pages/DashboardV2/Components/PreventativeMaintenance/timelineLayout';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const loading = ref(true);
// FilterBar finishes initializing its form right after we mount, which fires
// the deep watcher below and kicks off a second fetch with showLoading=false.
// That can flip `loading` to false while the first fetch is still in flight,
// briefly showing the empty state. Hold the skeleton until at least one
// response has landed.
const hasLoadedOnce = ref(false);
const groups = ref([]);
const yearOptions = ref([]);
const timelineRef = ref(null);
const visibleYears = ref(DEFAULT_VISIBLE_YEARS);
const windowStart = ref(new Date().getFullYear() - Math.floor(DEFAULT_VISIBLE_YEARS / 2));
// Sticky anchor for the band — matches the live bottom edge of DetailsPage's
// `.sticky.top-15` filter row. Falls back to a sensible single-line height
// until the ResizeObserver below catches up post-mount.
const DASHBOARD_FILTER_BAR_TOP = 60;
const DEFAULT_FILTER_BAR_HEIGHT = 96;
const stickyBandTop = ref(DASHBOARD_FILTER_BAR_TOP + DEFAULT_FILTER_BAR_HEIGHT);

const timelineWidth = ref(0);

const currentYear = computed(() => new Date().getFullYear());
const years = computed(() => Array.from({ length: visibleYears.value }, (_, i) => windowStart.value + i));
const canStepLeft = computed(() => years.value[0] > (yearOptions.value[0] ?? -Infinity));
const canStepRight = computed(() => years.value.at(-1) < (yearOptions.value.at(-1) ?? Infinity));

// The densest row on screen drives how wide a year column must be: monthly /
// weekly rows widen columns (and so reduce how many years are shown) to keep
// their label-dropped dots legible instead of overflowing.
const maxCellsPerYear = computed(() => {
    let max = 0;
    for (const group of groups.value) {
        for (const row of group.rows ?? []) {
            const perYear = {};
            for (const cell of row.cells ?? []) {
                perYear[cell.year] = (perYear[cell.year] ?? 0) + 1;
                if (perYear[cell.year] > max) {
                    max = perYear[cell.year];
                }
            }
        }
    }
    return max;
});
const yearColumnMin = computed(() => yearColumnMinForDensity(maxCellsPerYear.value));
const showSkeleton = computed(() => loading.value || !hasLoadedOnce.value);
const skeletonGridStyle = computed(() => ({ gridTemplateColumns: headerGridTemplate(visibleYears.value) }));
const skeletonRowStyle = computed(() => ({
    gridTemplateColumns: rowGridTemplate(visibleYears.value),
    marginLeft: `${ROW_HORIZONTAL_MARGIN_PX}px`,
    marginRight: `${ROW_HORIZONTAL_MARGIN_PX}px`,
}));

const dataUrl = computed(() => props.widgetData?.dataUrl);

// Filter changes, the 60s refresh poll, and mount can each kick off a fetch,
// so requests can overlap. Tag every call and only let the most recent one
// write state (or clear loading) — otherwise a slow earlier response could
// clobber a newer one.
let latestRequestId = 0;

const fetchData = async ({ showLoading = true } = {}) => {
    if (!dataUrl.value) {
        return;
    }
    const requestId = ++latestRequestId;
    loading.value = showLoading;
    try {
        const formData = props.filters?.form?.data?.() ?? {};
        const params = {};
        if (Array.isArray(formData.locations) && formData.locations.length) {
            params.locations = formData.locations;
        }
        if (Array.isArray(formData.maintainers) && formData.maintainers.length) {
            params.maintainers = formData.maintainers;
        }
        if (Array.isArray(formData.types) && formData.types.length) {
            params.types = formData.types;
        }
        const url = buildUrlWithQuery(dataUrl.value, params);
        const response = await $http.get(url);
        if (requestId !== latestRequestId) {
            return;
        }
        const payload = response.data ?? {};
        groups.value = payload.groups ?? [];
        yearOptions.value = payload.filterOptions?.years ?? [];
        // Only anchor on the first successful load — re-centering on every
        // fetch would yank the window back to the current year on each 60s
        // poll, work-order-update refresh, or filter change, discarding the
        // user's chevron navigation. (hasLoadedOnce flips in `finally`, so
        // it's still false here on the first response.)
        if (!hasLoadedOnce.value && typeof payload.window?.currentYear === 'number') {
            anchorAround(payload.window.currentYear);
        }
    } catch (error) {
        if (requestId !== latestRequestId) {
            return;
        }
        console.error('Failed to fetch preventative maintenance schedule:', error);
        groups.value = [];
    } finally {
        if (requestId === latestRequestId) {
            loading.value = false;
            hasLoadedOnce.value = true;
        }
    }
};

// Pin the visible window inside [minYear, maxYear - visibleYears + 1] so
// chevron paging or resize can never reveal year columns that have no data
// behind them. yearOptions reflects the backend slot-generation window.
const clampWindowStart = (start) => {
    const opts = yearOptions.value;
    if (!opts || opts.length === 0) {
        return start;
    }
    const minYear = opts[0];
    const maxStart = opts.at(-1) - visibleYears.value + 1;
    // If the data window is narrower than the visible count, pin to minYear.
    if (maxStart <= minYear) {
        return minYear;
    }
    return Math.max(minYear, Math.min(start, maxStart));
};

const anchorAround = (year) => {
    windowStart.value = clampWindowStart(year - Math.floor(visibleYears.value / 2));
};

const shiftYears = (deltaPages) => {
    windowStart.value = clampWindowStart(windowStart.value + deltaPages * visibleYears.value);
};

const applyVisibleYears = (next) => {
    if (next === visibleYears.value) {
        return;
    }
    const previousWindowEnd = windowStart.value + visibleYears.value - 1;
    visibleYears.value = next;
    // Keep the current year visible if it was; otherwise keep the window
    // roughly where it was so the user doesn't lose context. Re-clamp at
    // the end so growing the visible count can't push us past the data.
    const cy = currentYear.value;
    if (cy >= windowStart.value && cy <= previousWindowEnd) {
        anchorAround(cy);
    } else if (windowStart.value + visibleYears.value - 1 < previousWindowEnd) {
        windowStart.value = previousWindowEnd - visibleYears.value + 1;
    }
    windowStart.value = clampWindowStart(windowStart.value);
};

const handleRefresh = () => fetchData({ showLoading: false });

let resizeObserver = null;
let filterBarResizeObserver = null;

watch(
    () => props.filters?.form?.data?.(),
    () => fetchData({ showLoading: false }),
    { deep: true },
);

// Recompute visible years from the last measured width and the current
// density (yearColumnMin). Called on resize, on mount, and whenever the loaded
// data changes how dense the densest row is.
const recomputeVisibleYears = () => {
    if (timelineWidth.value > 0) {
        applyVisibleYears(computeVisibleYears(timelineWidth.value, yearColumnMin.value));
    }
};

// A denser dataset (after a fetch) widens columns → fewer visible years.
watch(yearColumnMin, recomputeVisibleYears);

onMounted(() => {
    fetchData();
    eventBus.$on(events.dashboardWidgetRefresh, handleRefresh);
    // WorkOrderDetailsModal emits this on successful save — refetch so a
    // status change (e.g. "Done") propagates to the timeline cell without
    // requiring a page reload.
    eventBus.$on(events.workOrderUpdated, handleRefresh);

    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(([entry]) => {
            if (!entry) {
                return;
            }
            timelineWidth.value = entry.contentRect.width;
            recomputeVisibleYears();
        });
    }

    // Observe once the timeline has mounted (after the loading skeleton goes away).
    watch(
        timelineRef,
        (el) => {
            if (resizeObserver && el) {
                resizeObserver.disconnect();
                resizeObserver.observe(el);
                timelineWidth.value = el.clientWidth;
                recomputeVisibleYears();
            }
        },
        { immediate: true, flush: 'post' },
    );

    // Track DetailsPage's sticky filter bar so the band keeps pinning flush
    // against its bottom edge even when the filter inputs wrap to a second
    // line (or the sidebar collapses, etc.).
    const filterBarEl = document.querySelector('.dashboard-header');
    if (filterBarEl) {
        const updateStickyTop = () => {
            stickyBandTop.value = DASHBOARD_FILTER_BAR_TOP + filterBarEl.offsetHeight;
        };
        updateStickyTop();
        if (typeof ResizeObserver !== 'undefined') {
            filterBarResizeObserver = new ResizeObserver(updateStickyTop);
            filterBarResizeObserver.observe(filterBarEl);
        }
    }
});

onBeforeUnmount(() => {
    eventBus.$off(events.dashboardWidgetRefresh, handleRefresh);
    eventBus.$off(events.workOrderUpdated, handleRefresh);
    resizeObserver?.disconnect();
    filterBarResizeObserver?.disconnect();
});
</script>

<style scoped>
/*
    WidgetCard wraps its body in `overflow-y: auto`, which makes the body a
    scroll context — position: sticky then pins to the body instead of the
    page. For the stacked dashboard layout (the only layout this widget runs
    under), the body never actually scrolls; the outer page does. Opt out of
    the inner overflow so the sticky band can pin to the viewport like
    DetailsPage's filter bar.
*/
:deep(.widget-card-body) {
    overflow: visible;
}
</style>
